import { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Upload, FileText, CheckCircle2, AlertCircle, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";

/** Two-step dialog for importing a legacy accounting CSV of open invoices.
 *  Step 1 = preview (server counts rows, warns on skips). Step 2 = commit,
 *  which returns a batch_id for rollback if the accountant spots a mistake.
 */
export function InvoiceImportDialog({ open, onOpenChange, onImported }) {
    const fileRef = useRef(null);
    const [preview, setPreview] = useState(null);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [lastBatch, setLastBatch] = useState(null);  // {batch_id, inserted}

    const reset = () => {
        setPreview(null); setFile(null); setLastBatch(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setBusy(true);
        const fd = new FormData();
        fd.append("file", f);
        try {
            const { data } = await api.post("/invoices/import/preview", fd);
            if (data.error) {
                toast.error(data.error);
                setPreview(null);
            } else {
                setPreview(data);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Preview failed");
        } finally { setBusy(false); }
    };

    const commit = async () => {
        if (!file) return;
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/invoices/import/commit", fd);
            toast.success(`Imported ${data.inserted} invoice${data.inserted === 1 ? "" : "s"}`);
            setLastBatch({ batch_id: data.batch_id, inserted: data.inserted });
            onImported?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Import failed");
        } finally { setBusy(false); }
    };

    const rollback = async () => {
        if (!lastBatch) return;
        if (!window.confirm(`Undo this import? Deletes ${lastBatch.inserted} invoice(s) and adjusts customer balances.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/invoices/import/rollback/${lastBatch.batch_id}`);
            toast.success(`Rolled back ${data.deleted} invoice(s)`);
            setLastBatch(null);
            onImported?.();
        } catch (err) {
            toast.error("Rollback failed");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
            <DialogContent className="max-w-3xl" data-testid="invoice-import-dialog">
                <DialogHeader>
                    <DialogTitle>Import legacy invoices</DialogTitle>
                    <DialogDescription>
                        Upload a CSV of open/outstanding invoices from your prior accounting system.
                        Customer balances update automatically. Nothing gets emailed to customers.
                    </DialogDescription>
                </DialogHeader>

                {!preview && !lastBatch && (
                    <div className="space-y-3">
                        <div className="rounded-md border border-border p-3 flex items-center justify-between text-xs">
                            <div>
                                <div className="font-medium">Need the exact column format?</div>
                                <div className="text-muted-foreground">Download our blank CSV template with one example row.</div>
                            </div>
                            <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/import/template.csv`}
                                download
                                data-testid="invoice-import-template-link"
                                className="text-primary hover:underline whitespace-nowrap font-medium"
                            >
                                Download template →
                            </a>
                        </div>
                        <div className="rounded-md border border-border p-3 flex items-center justify-between text-xs">
                            <div>
                                <div className="font-medium">Need account numbers for your customers?</div>
                                <div className="text-muted-foreground">Export every customer with their HaulerOS account number.</div>
                            </div>
                            <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/customers/export/accounts.csv`}
                                download
                                data-testid="customer-accounts-export-link"
                                className="text-primary hover:underline whitespace-nowrap font-medium"
                            >
                                Export accounts →
                            </a>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            <strong>Required columns:</strong> account_number, invoice_number, issued_date, due_date, amount
                            <br />
                            <strong>Optional:</strong> amount_paid, description, notes
                        </div>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".csv"
                            onChange={onFile}
                            className="hidden"
                            data-testid="invoice-import-file-input"
                        />
                        <Button
                            variant="outline"
                            className="w-full h-32 border-dashed"
                            onClick={() => fileRef.current?.click()}
                            disabled={busy}
                            data-testid="invoice-import-select-btn"
                        >
                            <Upload className="w-5 h-5 mr-2" />
                            {busy ? "Parsing…" : "Select CSV file"}
                        </Button>
                    </div>
                )}

                {preview && !lastBatch && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="rounded-md border p-3">
                                <div className="text-2xl font-bold">{preview.counts.total}</div>
                                <div className="text-xs text-muted-foreground">Rows in file</div>
                            </div>
                            <div className="rounded-md border p-3 border-emerald-500/30 bg-emerald-500/5">
                                <div className="text-2xl font-bold text-emerald-600">{preview.counts.ok}</div>
                                <div className="text-xs text-muted-foreground">Will import</div>
                            </div>
                            <div className="rounded-md border p-3 border-amber-500/30 bg-amber-500/5">
                                <div className="text-2xl font-bold text-amber-600">{preview.counts.skip}</div>
                                <div className="text-xs text-muted-foreground">Skipped</div>
                            </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            Total balance to add: <strong>{fmtMoney(preview.total_balance_to_add)}</strong> across customers.
                        </div>
                        <div className="max-h-64 overflow-y-auto border rounded-md">
                            <table className="w-full text-xs" data-testid="invoice-import-preview-table">
                                <thead className="bg-secondary/40 sticky top-0">
                                    <tr>
                                        <th className="text-left px-2 py-1.5">Row</th>
                                        <th className="text-left px-2 py-1.5">Account</th>
                                        <th className="text-left px-2 py-1.5">Invoice #</th>
                                        <th className="text-right px-2 py-1.5">Amount</th>
                                        <th className="text-right px-2 py-1.5">Balance</th>
                                        <th className="text-left px-2 py-1.5">Status / Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.sample.map((r, i) => (
                                        <tr key={i} className="border-t border-border">
                                            <td className="px-2 py-1">{r.row_num}</td>
                                            <td className="px-2 py-1">{r.account_number}</td>
                                            <td className="px-2 py-1">{r.invoice_number}</td>
                                            <td className="px-2 py-1 text-right tabular">{fmtMoney(r.amount || 0)}</td>
                                            <td className="px-2 py-1 text-right tabular">{r.status === "ok" ? fmtMoney(r.balance) : "—"}</td>
                                            <td className="px-2 py-1">
                                                {r.status === "ok" ? (
                                                    <span className="text-emerald-600 inline-flex items-center gap-1">
                                                        <CheckCircle2 className="w-3 h-3" />OK ({r.invoice_status})
                                                    </span>
                                                ) : (
                                                    <span className="text-amber-600 inline-flex items-center gap-1">
                                                        <AlertCircle className="w-3 h-3" />{r.reason}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={reset} data-testid="invoice-import-back-btn">
                                Choose different file
                            </Button>
                            <Button
                                onClick={commit}
                                disabled={busy || preview.counts.ok === 0}
                                data-testid="invoice-import-commit-btn"
                            >
                                {busy ? "Importing…" : `Import ${preview.counts.ok} invoice${preview.counts.ok === 1 ? "" : "s"}`}
                            </Button>
                        </div>
                    </div>
                )}

                {lastBatch && (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 text-emerald-600">
                            <CheckCircle2 className="w-5 h-5" />
                            <div>
                                <div className="font-semibold">Imported {lastBatch.inserted} invoice{lastBatch.inserted === 1 ? "" : "s"}</div>
                                <div className="text-xs text-muted-foreground">Batch ID: <code>{lastBatch.batch_id.slice(0, 8)}…</code></div>
                            </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <Button variant="ghost" onClick={rollback} disabled={busy}
                                    data-testid="invoice-import-rollback-btn">
                                <Undo2 className="w-3.5 h-3.5 mr-1" />
                                Undo import
                            </Button>
                            <Button onClick={() => onOpenChange(false)} data-testid="invoice-import-close-btn">
                                Done
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}


/** Small button that downloads today's QuickBooks CSV. Prints a note when
 *  the file is empty (no invoices issued today). */
export function QuickBooksExportButton({ variant = "outline" }) {
    const [busy, setBusy] = useState(false);
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

    const download = async () => {
        setBusy(true);
        try {
            const res = await api.get("/reports/quickbooks/export.csv",
                { params: { date }, responseType: "blob" });
            const count = res.headers["x-invoice-count"];
            if (count === "0") {
                toast.info("No invoices issued on that day yet — nothing to export.");
            } else {
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url; a.download = `qb_export_${date}.csv`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success(`Downloaded QB CSV — ${count} invoice(s)`);
            }
        } catch (e) {
            toast.error("Export failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="flex items-center gap-2">
            <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="text-xs px-2 py-1 rounded border border-input bg-transparent"
                data-testid="qb-export-date"
            />
            <Button variant={variant} size="sm" onClick={download} disabled={busy}
                    data-testid="qb-export-btn">
                <FileText className="w-3.5 h-3.5 mr-1" />
                {busy ? "Building…" : "QB Export"}
            </Button>
        </div>
    );
}
